import { z } from 'zod';
import { RECHTSTEXT_DOKUMENTE, type RechtstextDokument } from './portal';

/**
 * Einstellungen → Rechtstexte (lib_rechtstexte.php der PHP-Middleware): Vorlagen von DXL, eigene Fassungen
 * je Marktplatz mit Versionen und die Angaben für Platzhalter, die nicht aus „Marktplatz & Betreiber“ kommen.
 */

export const RECHTSTEXT_DOKUMENT_IDS = Object.keys(RECHTSTEXT_DOKUMENTE) as [RechtstextDokument, ...RechtstextDokument[]];
export const RechtstextDokumentSchema = z.enum(RECHTSTEXT_DOKUMENT_IDS);

/** Wofür das Dokument gilt (RT_DOKUMENTE['fuer']) */
export const RECHTSTEXT_FUER: Record<RechtstextDokument, string> = {
  haendlervertrag: 'Händler – wird bei der Registrierung akzeptiert',
  agb: 'Käufer im Marktplatz-Shop – Seite „AGB“',
  datenschutz: 'Marktplatz-Shop, Händlerportal und App',
};

/** Angaben, die nicht aus den Stammdaten kommen – Feld → [Bezeichnung, Vorgabe] (RT_ANGABEN) */
export const RECHTSTEXT_ANGABEN = {
  GERICHTSSTAND: ['Gerichtsstand (Ort)', ''],
  AUFSICHTSBEHOERDE: ['Zuständige Datenschutz-Aufsichtsbehörde', ''],
  BESCHWERDE_EMAIL: ['E-Mail für Beschwerden von Händlern', ''],
  DATENSCHUTZ_EMAIL: ['E-Mail für Datenschutzanfragen', ''],
  SHOP_DOMAIN: ['Domain des Marktplatz-Shops', ''],
  SORTIMENT: ['Sortiment (Satzende, z. B. „Mode und Accessoires“)', ''],
  ZAHLUNGSDIENSTLEISTER: ['Zahlungsdienstleister', 'Stripe'],
  ABRECHNUNGSZYKLUS: ['Abrechnungszyklus', 'monatlich'],
  AUSZAHLUNG_MINDESTBETRAG: ['Mindestbetrag für Auszahlungen', ''],
  VERSANDFRIST_WERKTAGE: ['Versandfrist (Werktage)', '2'],
  KULANZFRIST_TAGE: ['Kulanzfrist (Tage)', '3'],
  GRUNDGEBUEHR: ['Grundgebühr für Händler', 'keine'],
  PROVISION_SATZ: ['Provision in % (Modul A)', ''],
  RETOUREN_PROVISION: ['Einbehalt bei Rückabwicklung in %', ''],
  STORNO_PAUSCHALE: ['Pauschale je selbst verschuldet nicht ausgeführter Bestellung', ''],
} as const satisfies Record<string, readonly [string, string]>;
export type RechtstextAngabe = keyof typeof RECHTSTEXT_ANGABEN;
export const RECHTSTEXT_ANGABE_IDS = Object.keys(RECHTSTEXT_ANGABEN) as RechtstextAngabe[];

/** Höchstlängen wie in der PHP-Middleware */
export const RECHTSTEXT_MAX_ZEICHEN = 600_000;
export const RECHTSTEXT_MIN_TEXT = 200;
export const RECHTSTEXT_ANGABE_MAX = 300;

/** Nur übergebene Felder ändern; Werte werden gekürzt statt abgelehnt (rt_angaben_speichern) */
export const RechtstextAngabenSchema = z.object({
  angaben: z.object(
    Object.fromEntries(RECHTSTEXT_ANGABE_IDS.map((k) => [k, z.string().max(2000).transform((v) => v.trim().slice(0, RECHTSTEXT_ANGABE_MAX)).optional()])) as Record<
      RechtstextAngabe,
      z.ZodOptional<z.ZodEffects<z.ZodString, string, string>>
    >,
  ),
});
export type RechtstextAngabenEingabe = z.input<typeof RechtstextAngabenSchema>;

export const RechtstextSpeichernSchema = z.object({
  html: z.string().max(RECHTSTEXT_MAX_ZEICHEN * 2, 'Der Text ist zu lang (höchstens 600 KB).'),
});

/** Vorschau: ungespeicherter Text aus dem Editor (oder eine ältere Fassung); ohne html die aktuelle Fassung */
export const RechtstextVorschauSchema = z.object({
  html: z.string().max(RECHTSTEXT_MAX_ZEICHEN * 2).optional(),
  modell: z.enum(['vermittlung', 'eigenhandel']).optional(),
});

export type PlatzhalterQuelle = 'Stammdaten' | 'Angaben' | 'Registrierung' | 'automatisch';

export interface RechtstextDokumentInfo {
  dokument: RechtstextDokument;
  titel: string;
  fuer: string;
  version: string;
  stand: string;
  angepasst: boolean;
  /** SHA-256 des gespeicherten Quelltexts */
  sha256: string;
  geaendertAm: string | null;
  von: string;
  versionen: number;
  /** Platzhalter ohne Wert (Bezeichnungen) */
  fehlend: string[];
  /** öffentliche Adresse der aktuellen Fassung (für Shop und Portal) */
  url: string;
  /** Vorlage von DXL: gültige Version und ob sie neuer ist als die, auf der dieser Text beruht */
  vorlage: { version: string; stand: string; basis: string; neu: boolean };
}

export interface RechtstextUebersicht {
  dokumente: RechtstextDokumentInfo[];
  angaben: { key: RechtstextAngabe; name: string; wert: string; vorgabe: string }[];
  platzhalter: { key: string; name: string; wert: string; quelle: PlatzhalterQuelle }[];
}

export interface RechtstextVersion {
  version: string;
  stand: string;
  angepasst: boolean;
  sha256: string;
  erstelltAm: string | null;
  von: string;
}

/** Quelltext einer Fassung mit Platzhaltern – für den Editor */
export interface RechtstextFassung extends RechtstextVersion {
  dokument: RechtstextDokument;
  titel: string;
  html: string;
}

export interface RechtstextVersionSeite {
  eintraege: RechtstextVersion[];
  weiter: string | null;
}

export interface RechtstextSpeichernAntwort {
  fassung: RechtstextFassung;
  /** Text war identisch – keine neue Version */
  unveraendert: boolean;
}
